/**
 * One AI workflow, rendered wherever a scope row says it belongs.
 *
 * The server decides everything about this component except how it looks: whether it exists
 * at all, what its button says, which heading sits above the answer. `GET /v1/profile/`
 * answers with a matched profile's `ui_components` or with `no_config`, and `no_config`
 * renders NOTHING — no button, no empty box, no explanation. An academy that has not turned
 * AI on should not be able to tell this component was compiled in.
 *
 * WHY NOT UPSTREAM'S COMPONENT. `ConfigurableAIAssistance` does the same job in the same
 * order, but it is built on Paragon and `@edx/frontend-platform`, it reads its course and
 * unit ids by running regexes over `window.location.pathname`, and it renders into MFE plugin
 * slots we do not have. We have the ids in route params and our own design tokens, so the
 * borrowing stops at the contract.
 *
 * THE ANSWER IS TEXT, AND IS RENDERED AS TEXT. Models emit markdown and this shows it
 * literally, asterisks and all — deliberately, for now: the alternative is a markdown
 * renderer on model output, and the version of that mistake worth avoiding forever is
 * `dangerouslySetInnerHTML`. A renderer can be added; an XSS through an LLM cannot be undone.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Banner, Icon } from '../ui';
import {
  AI_ACTIONS,
  aiDisabledForTenant,
  explainAiFailure,
  getAiProfile,
  runWorkflow,
  type AiContext,
} from '../lib/ai';
import './ai.css';

/*
 * `data-testid` on five nodes, and they are load-bearing rather than convenient.
 *
 * The E2E suite asserts on the STREAM — that text grows, that the busy state clears, that
 * provenance appears only after the answer finishes. Those assertions need to name the
 * output node exactly, and every other way of naming it is a lie waiting to happen: class
 * names are ours to restyle, the heading text comes from a database row an operator can
 * edit, and role/name lookups cannot distinguish this box from the unit above it. See
 * tests/e2e/tests/ai-unit-summary.spec.ts.
 */

type Phase = 'idle' | 'streaming' | 'done' | 'error';

/**
 * Component names from the profile that this file knows how to draw.
 *
 * A pair of sets rather than a name-to-component registry, because there is exactly one
 * shape of each so far. The registry earns its keep at the second shape — `AISidebarResponse`
 * is a chat panel, not a longer version of this — and adding it now would be indirection
 * with one entry. What matters already is the GUARD: a profile naming a component we have not
 * built renders nothing, rather than throwing inside someone's course.
 */
const KNOWN_REQUEST = new Set(['AIRequestComponent']);
const KNOWN_RESPONSE = new Set(['AIResponseComponent']);

export function AiPanel({ slot, courseId, locationId }: {
  slot: string;
  courseId?: string;
  locationId?: string;
}) {
  const ctx = useMemo<AiContext>(
    () => ({ slot, courseId, locationId }),
    [slot, courseId, locationId],
  );

  const profile = useQuery({
    queryKey: ['ai-profile', slot, courseId ?? '', locationId ?? ''],
    queryFn: () => getAiProfile(ctx),
    enabled: !aiDisabledForTenant(),
    // One attempt. This is an enhancement: a retry storm behind a button nobody has pressed
    // is pure cost, and the failure mode of not retrying is a missing button.
    retry: false,
    // The scope rows behind this change when an operator edits them, not while someone reads.
    staleTime: 5 * 60 * 1000,
  });

  const [phase, setPhase] = useState<Phase>('idle');
  const [output, setOutput] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const stop = useCallback(() => inFlight.current?.abort(), []);

  const start = useCallback(async () => {
    // Abort any previous run before starting: two live streams would interleave their tokens
    // into one box, which reads as the model losing its mind rather than as a bug.
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;

    setOutput('');
    setFailure(null);
    setPhase('streaming');

    // Tracked locally rather than read back off state, so the abort branch below does not
    // depend on a render having happened first.
    let received = false;

    try {
      const text = await runWorkflow(
        ctx,
        { action: AI_ACTIONS.RUN },
        {
          signal: controller.signal,
          onChunk: (chunk) => {
            received = true;
            setOutput((prev) => prev + chunk);
          },
        },
      );
      // A non-streaming profile never calls `onChunk` and returns the whole answer instead.
      // Which of the two a workflow is is a database row, so both paths have to land here.
      if (!received && text) setOutput(text);
      setPhase('done');
    } catch (error) {
      // Stopping is a choice, not a failure: keep whatever arrived and say nothing.
      if (controller.signal.aborted) {
        setPhase(received ? 'done' : 'idle');
        return;
      }
      setFailure(explainAiFailure(error));
      setPhase('error');
    } finally {
      if (inFlight.current === controller) inFlight.current = null;
    }
  }, [ctx]);

  if (aiDisabledForTenant() || !profile.data) return null;

  const request = profile.data.ui_components.request;
  const response = profile.data.ui_components.response;
  if (!request || !KNOWN_REQUEST.has(request.component)) return null;

  const buttonText = request.config.buttonText || 'Ask AI';
  const heading =
    (response && KNOWN_RESPONSE.has(response.component) && response.config.customMessage) ||
    request.config.customMessage ||
    buttonText;

  const streaming = phase === 'streaming';
  const hasOutput = output.length > 0;

  return (
    <section className="ai-panel" aria-label={heading} data-testid="ai-panel">
      <div className="ai-panel__head">
        <p className="ai-panel__label">
          <Icon name="auto_awesome" />
          {heading}
        </p>

        <div className="ai-panel__actions" data-testid="ai-actions">
          {!streaming && (
            <button type="button" className="ui-btn" data-variant="secondary" onClick={start}>
              {hasOutput || phase === 'error' ? 'Try again' : buttonText}
            </button>
          )}
          {streaming && (
            <button type="button" className="ui-btn" data-variant="ghost" onClick={stop}>
              <Icon name="stop_circle" />
              Stop
            </button>
          )}
        </div>
      </div>

      {(hasOutput || streaming) && (
        <div
          className="ai-panel__out"
          data-testid="ai-output"
          // NOT a live region. Announcing a token stream to a screen reader is a firehose;
          // the one-line status below announces the transitions instead, which is the part
          // that carries information.
          aria-busy={streaming || undefined}
          data-streaming={streaming || undefined}
        >
          {output}
          {streaming && <span className="ai-panel__caret" aria-hidden="true" />}
        </div>
      )}

      {failure && <Banner tone="warning">{failure}</Banner>}

      {hasOutput && !streaming && (
        <p className="ai-panel__provenance" data-testid="ai-provenance">
          <Icon name="info" />
          Written by AI from this unit&rsquo;s content. It can be wrong — check it against the
          unit above before you rely on it.
        </p>
      )}

      <p className="ai-panel__status" role="status" data-testid="ai-status">
        {streaming ? 'Generating an answer…' : ''}
        {phase === 'done' && hasOutput ? 'Answer complete.' : ''}
        {phase === 'error' && failure ? failure : ''}
      </p>
    </section>
  );
}
